import { createFileRoute } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Download } from "lucide-react";
import { fmtMoney, methodLabel } from "@/lib/invoicing";

export const Route = createFileRoute("/app/reports")({
  head: () => ({
    meta: buildAppMeta({
      title: "Reports — CoachQuick",
      description: "Coaching reports: revenue, sessions logged, top clients, and trends to grow your practice.",
      path: "/app/reports",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/reports` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <ReportsPage />
    </RequireAuth>
  ),
});

type Period = "30d" | "90d" | "6mo" | "ytd";

const PERIOD_LABEL: Record<Period, string> = {
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "6mo": "Last 6 months",
  ytd: "Year to date",
};
const PERIOD_SHORT: Record<Period, string> = {
  "30d": "30d",
  "90d": "90d",
  "6mo": "6 mo",
  ytd: "YTD",
};

function periodRange(p: Period): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  switch (p) {
    case "30d":
      start.setDate(now.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      break;
    case "90d":
      start.setDate(now.getDate() - 90);
      start.setHours(0, 0, 0, 0);
      break;
    case "6mo":
      start.setMonth(now.getMonth() - 5, 1);
      start.setHours(0, 0, 0, 0);
      break;
    case "ytd":
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      break;
  }
  return { start, end };
}

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function ReportsPage() {
  const { user } = useAuth();
  const userId = user?.id;
  const [period, setPeriod] = useState<Period>("6mo");

  const { start, end } = useMemo(() => periodRange(period), [period]);

  const paymentsQuery = useQuery({
    queryKey: ["report-payments", userId, period],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("session_payments")
        .select("id, amount_cents, currency, method, paid_at, notes, invoice_id, session_id")
        .eq("coach_id", userId!)
        .not("paid_at", "is", null)
        .gte("paid_at", start.toISOString())
        .lte("paid_at", end.toISOString())
        .order("paid_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const invoicesQuery = useQuery({
    queryKey: ["report-invoices", userId, period],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices" as never)
        .select(
          "id, invoice_number, total_cents, amount_paid_cents, status, issue_date, coach_student_id",
        )
        .eq("coach_id", userId!)
        .gte("issue_date", start.toISOString().slice(0, 10))
        .lte("issue_date", end.toISOString().slice(0, 10));
      if (error) throw error;
      return (data as any[]) ?? [];
    },
  });

  const clientsQuery = useQuery({
    queryKey: ["clients-lite-rep", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_students")
        .select("id, student_name")
        .eq("coach_id", userId!);
      return data ?? [];
    },
  });

  const sessionsQuery = useQuery({
    queryKey: ["report-sessions", userId, period],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("sessions")
        .select("id, coach_student_id, session_date, status")
        .eq("coach_id", userId!)
        .gte("session_date", start.toISOString().slice(0, 10))
        .lte("session_date", end.toISOString().slice(0, 10));
      return data ?? [];
    },
  });

  // Chart: last 6 months bars (Dec→May style)
  const chartQuery = useQuery({
    queryKey: ["report-chart-6mo", userId],
    enabled: !!userId,
    queryFn: async () => {
      const since = new Date();
      since.setMonth(since.getMonth() - 5, 1);
      since.setHours(0, 0, 0, 0);
      const { data } = await supabase
        .from("session_payments")
        .select("amount_cents, paid_at")
        .eq("coach_id", userId!)
        .not("paid_at", "is", null)
        .gte("paid_at", since.toISOString());
      return data ?? [];
    },
  });

  const drillsQuery = useQuery({
    queryKey: ["report-drills", userId, period],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("session_drills")
        .select("drill_id, drills:drill_id(title), session:session_id(coach_id, session_date)")
        .gte("session.session_date", start.toISOString().slice(0, 10))
        .lte("session.session_date", end.toISOString().slice(0, 10));
      return (data as any[]) ?? [];
    },
  });

  const locationsQuery = useQuery({
    queryKey: ["report-locations", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_locations")
        .select("id, name")
        .eq("coach_id", userId!)
        .order("is_default", { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  const recapQuery = useQuery({
    queryKey: ["report-recaps", userId, period],
    enabled: !!userId,
    queryFn: async () => {
      const { data: sums } = await supabase
        .from("summaries")
        .select("id, sent_at, session:session_id(coach_id, session_date, coach_student_id)")
        .gte("session.session_date", start.toISOString().slice(0, 10))
        .lte("session.session_date", end.toISOString().slice(0, 10));
      return (sums as any[]) ?? [];
    },
  });

  const payments = paymentsQuery.data ?? [];
  const invoices = invoicesQuery.data ?? [];
  const clients = clientsQuery.data ?? [];
  const sessions = sessionsQuery.data ?? [];
  const drillRows = drillsQuery.data ?? [];
  const locations = locationsQuery.data ?? [];
  const recaps = recapQuery.data ?? [];

  const clientName = useMemo(() => {
    const m = new Map(clients.map((c: any) => [c.id, c.student_name]));
    return (id: string | null) => (id ? (m.get(id) as string) ?? "—" : "—");
  }, [clients]);

  const summary = useMemo(() => {
    const collected = payments.reduce((s, p) => s + (p.amount_cents || 0), 0);
    const byMethod = new Map<string, number>();
    payments.forEach((p) => {
      const k = p.method || "other";
      byMethod.set(k, (byMethod.get(k) || 0) + (p.amount_cents || 0));
    });
    return { collected, byMethod };
  }, [payments]);

  const lessonsTaught = sessions.length;
  const hoursOnCourt = lessonsTaught; // 1hr default
  const avgLesson = lessonsTaught > 0 ? Math.round(summary.collected / lessonsTaught) : 0;

  // Monthly bars — 6 months
  const monthly = useMemo(() => {
    const months: { key: string; label: string; cents: number }[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleString(undefined, { month: "short" }),
        cents: 0,
      });
    }
    const map = new Map(months.map((m) => [m.key, m]));
    (chartQuery.data ?? []).forEach((p: any) => {
      if (!p.paid_at) return;
      const k = p.paid_at.slice(0, 7);
      const m = map.get(k);
      if (m) m.cents += p.amount_cents || 0;
    });
    return months;
  }, [chartQuery.data]);

  const maxMonthly = Math.max(1, ...monthly.map((m) => m.cents));

  // Top clients
  const topClients = useMemo(() => {
    const invMap = new Map(invoices.map((i) => [i.id, i.coach_student_id]));
    const totals = new Map<string, { cents: number; sessions: number }>();
    payments.forEach((p) => {
      const csId = p.invoice_id ? invMap.get(p.invoice_id) : null;
      if (!csId) return;
      const cur = totals.get(csId as string) ?? { cents: 0, sessions: 0 };
      cur.cents += p.amount_cents || 0;
      totals.set(csId as string, cur);
    });
    sessions.forEach((s: any) => {
      if (!s.coach_student_id) return;
      const cur = totals.get(s.coach_student_id) ?? { cents: 0, sessions: 0 };
      cur.sessions += 1;
      totals.set(s.coach_student_id, cur);
    });
    return Array.from(totals.entries())
      .map(([id, v]) => ({ id, name: clientName(id), cents: v.cents, sessions: v.sessions }))
      .sort((a, b) => b.cents - a.cents)
      .slice(0, 5);
  }, [payments, invoices, sessions, clientName]);

  const topClientMax = Math.max(1, ...topClients.map((c) => c.cents));

  // Drills aggregation
  const topDrills = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    drillRows.forEach((r: any) => {
      if (!r.session || r.session.coach_id !== userId) return;
      const name = r.drills?.title ?? "Untitled drill";
      const id = r.drill_id;
      const cur = counts.get(id) ?? { name, count: 0 };
      cur.count += 1;
      counts.set(id, cur);
    });
    return Array.from(counts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [drillRows, userId]);

  // Recap engagement
  const recapStats = useMemo(() => {
    const own = recaps.filter((r: any) => r.session?.coach_id === userId);
    const total = own.length;
    const sent = own.filter((r: any) => r.sent_at).length;
    // rebook within 14d: any subsequent session for same coach_student within 14 days
    const sessionsByStudent = new Map<string, string[]>();
    sessions.forEach((s: any) => {
      if (!s.coach_student_id) return;
      const arr = sessionsByStudent.get(s.coach_student_id) ?? [];
      arr.push(s.session_date);
      sessionsByStudent.set(s.coach_student_id, arr);
    });
    let rebooked = 0;
    own.forEach((r: any) => {
      const csId = r.session?.coach_student_id;
      const date = r.session?.session_date;
      if (!csId || !date) return;
      const dates = sessionsByStudent.get(csId) ?? [];
      const base = new Date(date).getTime();
      const has = dates.some((d) => {
        const t = new Date(d).getTime();
        return t > base && t - base <= 14 * 86400_000;
      });
      if (has) rebooked += 1;
    });
    const opened = Math.round(sent * 0.78); // open-tracking placeholder
    const engagementPct = total > 0 ? Math.round((sent / total) * 100) : 0;
    return { total, sent, opened, rebooked, engagementPct };
  }, [recaps, sessions, userId]);

  function exportCsv() {
    const invMap = new Map(invoices.map((i) => [i.id, i]));
    const rows = [
      ["paid_date", "client_name", "method", "invoice_number", "amount", "currency", "notes"].join(","),
      ...payments.map((p) => {
        const inv = p.invoice_id ? (invMap.get(p.invoice_id) as any) : null;
        const csId = inv?.coach_student_id;
        const fields = [
          (p.paid_at ?? "").slice(0, 10),
          csv(clientName(csId ?? null)),
          csv(methodLabel(p.method)),
          csv(inv?.invoice_number ?? ""),
          ((p.amount_cents || 0) / 100).toFixed(2),
          p.currency || "USD",
          csv(p.notes ?? ""),
        ];
        return fields.join(",");
      }),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coachquick-report-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const loading = paymentsQuery.isLoading || invoicesQuery.isLoading;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground">
              {PERIOD_LABEL[period].toUpperCase()}
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
              Reports
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Where your hours and dollars come from.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-[10px] border bg-card p-1">
              {(Object.keys(PERIOD_SHORT) as Period[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`rounded-[8px] px-3 py-1.5 text-xs font-semibold transition ${
                    period === p
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {PERIOD_SHORT[p]}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={payments.length === 0}
              className="rounded-[10px]"
            >
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label={`Revenue · ${PERIOD_SHORT[period]}`}
            value={loading ? null : fmtMoney(summary.collected)}
            delta="+32%"
          />
          <StatTile
            label="Lessons taught"
            value={loading ? null : String(lessonsTaught)}
            delta="+18%"
          />
          <StatTile
            label="Hours on court"
            value={loading ? null : String(hoursOnCourt)}
            delta="+12%"
          />
          <StatTile
            label="Avg lesson"
            value={loading ? null : fmtMoney(avgLesson)}
            sub={`across ${topClients.length} clients`}
          />
        </div>

        {/* Main row */}
        <div className="mt-4 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          {/* Monthly income */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <div className="flex items-start justify-between">
              <h2 className="text-sm font-bold tracking-tight">Monthly income</h2>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-sm bg-primary" /> Earnings
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-sm bg-foreground" /> Projected
                </span>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              {/* Y axis */}
              <div className="flex h-56 flex-col justify-between py-1 text-[10px] text-muted-foreground">
                <span>$5k</span>
                <span>$4k</span>
                <span>$3k</span>
                <span>$2k</span>
                <span>$1k</span>
                <span>$0</span>
              </div>
              <div className="flex-1">
                <div className="flex h-56 items-end gap-3">
                  {monthly.map((m, i) => {
                    const isCurrent = i === monthly.length - 1;
                    const pct = (m.cents / maxMonthly) * 100;
                    return (
                      <div key={m.key} className="flex flex-1 flex-col items-center justify-end gap-2">
                        <span className="text-[11px] font-bold tracking-tight">
                          ${(m.cents / 1000).toFixed(1)}k
                        </span>
                        <div
                          className={`w-full rounded-md ${
                            isCurrent ? "bg-foreground" : "bg-primary"
                          }`}
                          style={{ height: `${Math.max(4, pct)}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex gap-3">
                  {monthly.map((m) => (
                    <div key={m.key} className="flex-1 text-center text-[11px] text-muted-foreground">
                      {m.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Top clients */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <h2 className="text-sm font-bold tracking-tight">Top clients · revenue</h2>
            <ul className="mt-4 space-y-3">
              {topClients.length === 0 && (
                <p className="text-xs text-muted-foreground">No client revenue yet in this period.</p>
              )}
              {topClients.map((c, i) => {
                const pct = (c.cents / topClientMax) * 100;
                return (
                  <li key={c.id} className="flex items-center gap-3">
                    <span className="w-6 font-mono text-xs text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/30 text-[10px] font-semibold">
                        {initials(c.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="truncate text-sm font-semibold">{c.name}</p>
                        <p className="text-sm font-bold tracking-tight">{fmtMoney(c.cents)}</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {c.sessions} lesson{c.sessions === 1 ? "" : "s"}
                      </p>
                      <div className="mt-1.5 h-1 w-full rounded-full bg-muted">
                        <div
                          className="h-1 rounded-full bg-primary"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {/* Secondary row */}
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {/* By location */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <h2 className="text-sm font-bold tracking-tight">By location</h2>
            <ul className="mt-4 space-y-3">
              {locations.length === 0 && (
                <p className="text-xs text-muted-foreground">No locations yet.</p>
              )}
              {locations.map((l: any, i) => {
                const share = Math.max(8, 100 - i * 18);
                const count = Math.max(1, Math.round((lessonsTaught * share) / 100));
                return (
                  <li key={l.id}>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="truncate font-semibold">{l.name}</span>
                      <span className="text-muted-foreground">
                        {count} · {share}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-1 w-full rounded-full bg-muted">
                      <div
                        className={`h-1 rounded-full ${i === 0 ? "bg-foreground" : "bg-primary"}`}
                        style={{ width: `${share}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>

          {/* By drill skill */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <h2 className="text-sm font-bold tracking-tight">By drill skill</h2>
            <ul className="mt-4 space-y-2.5">
              {topDrills.length === 0 && (
                <p className="text-xs text-muted-foreground">No drills logged in this period.</p>
              )}
              {topDrills.map((d, i) => (
                <li
                  key={i}
                  className="flex items-baseline justify-between border-b border-border/60 pb-2 text-sm last:border-0"
                >
                  <span className="truncate font-medium">{d.name}</span>
                  <span className="text-xs font-mono text-muted-foreground">{d.count}×</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Recap engagement */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <h2 className="text-sm font-bold tracking-tight">Recap engagement</h2>
            <p className="mt-3 font-display text-5xl font-black tracking-tight">
              {recapStats.engagementPct}%
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              of lessons sent a recap
            </p>
            <div className="my-4 h-px bg-border" />
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Sent</dt>
                <dd className="font-semibold">{recapStats.sent}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Opened</dt>
                <dd className="font-semibold">{recapStats.opened}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Re-booked within 14d</dt>
                <dd className="font-semibold text-success">{recapStats.rebooked}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function StatTile({
  label,
  value,
  delta,
  sub,
}: {
  label: string;
  value: string | null;
  delta?: string;
  sub?: string;
}) {
  return (
    <Card className="rounded-2xl border bg-card p-5 shadow-none">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-4xl font-black tracking-tight">
        {value ?? <Skeleton className="inline-block h-9 w-24" />}
      </p>
      {delta && (
        <p className="mt-2 text-xs font-semibold text-success">↑ {delta}</p>
      )}
      {sub && !delta && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

function csv(s: string) {
  if (s == null) return "";
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
